import { Heading } from "@/components/ui/heading";
import { CloseIcon, Icon } from "@/components/ui/icon";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { ComponentPropsWithoutRef, useState } from "react";
import { Button, ButtonIcon, ButtonText } from "@/components/ui/button";
import QRCodeStyled from "react-native-qrcode-styled";
import { useQRCodeDownloader } from "@/utils/useQRCode";
import { HStack } from "@/components/ui/hstack";
import { Copy } from "lucide-react-native";
import { Box } from "@/components/ui/box";
import { Link as ExpoLink } from "expo-router";
import { Image } from "@/components/ui/image";
import { useMobileBreakpoint } from "@/utils/useMobileBreakpoint";

const APP_STORE_LINK = process.env.EXPO_PUBLIC_APP_STORE_LINK!;
const PLAY_STORE_LINK = process.env.EXPO_PUBLIC_PLAY_STORE_LINK!;

function StoreButton({ link, source }: { link: string; source: string }) {
  const bp = useMobileBreakpoint();
  const [isOpen, setOpen] = useState(false);

  return (
    <>
      <ExpoLink
        href={link as any}
        onPress={(e) => {
          if (bp === "desktop") {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <Image source={source} className="w-[150px] h-[45px]" alt="store-button" />
      </ExpoLink>
      <AppStoreButtonsModal
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        arbitraryUrl={link}
      />
    </>
  );
}

export function AppStoreButtons(props: ComponentPropsWithoutRef<typeof Box>) {
  return (
    <Box
      className="flex flex-col sm:flex-row gap-4 self-center mt-10"
      {...props}
    >
      <StoreButton
        link={APP_STORE_LINK}
        source={require("@/assets/auth/app-store.png")}
      />
      <StoreButton
        link={PLAY_STORE_LINK}
        source={require("@/assets/auth/google-play.png")}
      />
    </Box>
  );
}

export function AppStoreButtonsModal({
  arbitraryUrl,
  ...props
}: ComponentPropsWithoutRef<typeof Modal> & {
  arbitraryUrl: string;
}) {
  const { qrCodeRef, onCopy, link } = useQRCodeDownloader({
    arbitraryUrl: arbitraryUrl,
  });

  return (
    <Modal avoidKeyboard isOpen={props.isOpen} onClose={props.onClose}>
      <ModalBackdrop />
      <ModalContent>
        <ModalHeader>
          <Heading>Download GoTips App</Heading>
          <ModalCloseButton>
            <Icon
              as={CloseIcon}
              size="md"
              className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
            />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody contentContainerClassName="items-center">
          <QRCodeStyled
            ref={qrCodeRef}
            data={link}
            padding={20}
            pieceSize={8}
          />
        </ModalBody>
        <ModalFooter className="flex-col items-center">
          <HStack className="gap-4">
            <Button onPress={onCopy} variant="outline">
              <ButtonIcon as={Copy} size={20} className="text-typography-950" />
              <ButtonText>Copy link</ButtonText>
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
